import DatePickerNative from '@/components/UI/DatePickerNative/DatePickerNative';
import FormField from '@/components/UI/FormFields/Latest/FormField';
import type { components, operations } from '@/generated/internal-api/openapi-types';
import { successToast, trunc } from '@/helpers';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC, useCallback, useEffect, useMemo, useState, memo } from 'react';
import { FormProvider, useForm, type FieldErrors } from 'react-hook-form-latest';
import { DateUtils } from '@/utils/Date.utils.ts';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import SearchSelect from '@/components/UI/SearchSelect/SearchSelect';
import { type BenchlingBackfillEntityType } from '@/model/BenchlingBackfill.model.ts';
import { useDebouncedCallback } from 'use-debounce';
import { StudyLookup } from '@/components/UI/StudyLookup/StudyLookup';
import type { StudyApiId, StudyInfoV1 } from '@/model/Study.model';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { _isEmpty, _notNil } from '@/littledash.ts';
import { formatItemCount } from './CreateBackfill.utils';
import styles from './CreateBackfill.module.scss';
import { RouteService } from '@/support/RouteService';

interface CreateBackfillProps {
  closeModal: () => void;
  onCreateBackfill: () => void;
}

// Use the schema types directly without modification
type BackfillFormSchema = components['schemas']['BenchlingBackfillCreateV1.schema'];

// Use OpenAPI spec for parameter types
type BackfillCountQueryParams = Pick<
  NonNullable<operations['benchling-backfill-count']['parameters']['query']>,
  'from' | 'to' | 'entity_types' | 'study_api_ids'
>;

// Define a type for the onSelect event
interface SelectEvent {
  type: 'ADDED' | 'REMOVED';
  value: BenchlingBackfillEntityType;
}

// Custom display values for entity types
const entityTypeDisplayMap: Record<BenchlingBackfillEntityType, string> = {
  studies: 'Studies',
  treatments: 'Treatments',
  study_groups: 'Groups',
  animals: 'Animals',
  samples: 'Samples',
  measurements: 'Measurements',
  dosages: 'Dosages',
  observations: 'Observations',
} as const;

// Pure function to determine if a study should be disabled based on benchling_folder_id
const isStudyDisabled = (study: StudyInfoV1): boolean => {
  return _isEmpty(study.benchling_folder_id);
};

export const CreateBackfill: FC<CreateBackfillProps> = memo(({ onCreateBackfill, closeModal }) => {
  // Keep the selected studies in local state to display them - mapped by API ID for easy access and uniqueness
  const [selectedStudiesMap, setSelectedStudiesMap] = useState<Record<StudyApiId, StudyInfoV1>>({});

  // Track if component is mounted to prevent memory leaks
  const isMounted = useMountedState();

  const formMethods = useForm<BackfillFormSchema>({
    defaultValues: {
      date_from: DateUtils.dateNow(),
      date_to: DateUtils.dateNow(),
      entity_types: Array<BenchlingBackfillEntityType>(),
      study_api_ids: Array<StudyApiId>(),
    },
    mode: 'onChange',
    resolver: (values, _context, _options) => {
      const errors: FieldErrors<BackfillFormSchema> = {};

      if (!values.date_from) {
        errors.date_from = {
          type: 'required',
          message: 'From date is required',
        };
      }

      if (!values.date_to) {
        errors.date_to = {
          type: 'required',
          message: 'To date is required',
        };
      }

      if (values.date_from && values.date_to && values.date_from > values.date_to) {
        errors.date_to = {
          type: 'validate',
          message: 'To date must be after From date',
        };
      }

      if (!values.entity_types?.length) {
        errors.entity_types = {
          type: 'required',
          message: 'At least one entity type must be selected',
        };
      }

      if (Object.keys(errors).length > 0) {
        return { values: {} as Record<string, never>, errors };
      }
      return { values, errors: {} as Record<string, never> };
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = formMethods;

  const [apiError, setApiError] = useState<Error | false>(false);

  // Use direct array destructuring for form values
  const [dateFrom, dateTo, entityTypes, studyApiIds] = watch(['date_from', 'date_to', 'entity_types', 'study_api_ids']);

  const hasSelectedEntityTypes = useMemo(() => !!entityTypes?.length, [entityTypes]);

  // Track when we've initiated a request that hasn't completed yet
  const [pendingRequest, setPendingRequest] = useState(false);

  const {
    response: countResponse,
    invoke: getCount,
    loading: isCountLoading,
  } = useApiHook({
    endpoint: 'GET /api/v1/backfill/count',
    invokeOnInit: false,
  });

  const { invoke: createBackfill, loading: createBackfillLoading } = useApiHook({
    endpoint: 'POST /api/v1/backfill',
    invokeOnInit: false,
    options: { onError: { toast: true, slug: 'create-backfill' } },
  });

  // Generate entity type options using API response item_counts when available
  const entityTypeSections = useMemo(() => {
    const options = Object.entries(entityTypeDisplayMap).map(([value, title]) => {
      // Only show counts when API returns item_counts structure AND the entity type is currently selected
      let count = 0;

      if (countResponse?.body?.item_counts && entityTypes?.includes(value as BenchlingBackfillEntityType)) {
        count = countResponse.body.item_counts[value] || 0;
      }

      return {
        id: value,
        title,
        meta: formatItemCount(count),
      };
    });

    return [{ items: options }];
  }, [countResponse?.body, entityTypes]);

  // Create a debounced function to fetch count - critical to prevent excessive API calls
  const debouncedFetchCount = useDebouncedCallback(
    async (params: BackfillCountQueryParams) => {
      try {
        await getCount({ query: params });
      } finally {
        // Only update state if the component is still mounted
        if (isMounted()) {
          setPendingRequest(false);
        }
      }
    },
    200, // 200ms debounce - balance between responsiveness and server load
    { maxWait: 1000 } // Maximum wait of 1 second
  );

  // Set up the cleanup function to update mounted state and cancel pending calls on unmount
  useEffect(() => {
    return () => {
      debouncedFetchCount.cancel(); // Cancel any pending debounced calls
    };
  }, [debouncedFetchCount]);

  // Effect for fetching count data when form values change
  useEffect(() => {
    if (!dateFrom || !dateTo || errors.date_from || errors.date_to || !hasSelectedEntityTypes) {
      setPendingRequest(false);
      return;
    }

    // Set pending state immediately when form values change
    setPendingRequest(true);

    const params: BackfillCountQueryParams = {
      from: dateFrom,
      to: dateTo,
      entity_types: entityTypes,
    };

    // Add study_api_ids if studies are selected
    if (studyApiIds && studyApiIds.length > 0) {
      params.study_api_ids = studyApiIds;
    }

    // Use debounced function to limit API calls
    debouncedFetchCount(params);
  }, [
    dateFrom,
    dateTo,
    entityTypes,
    studyApiIds,
    errors.date_from,
    errors.date_to,
    hasSelectedEntityTypes,
    debouncedFetchCount,
  ]);

  // Extract count value from API response for reuse
  const itemCount = useMemo(() => countResponse?.body?.count ?? 0, [countResponse?.body?.count]);

  // Check if count exceeds maximum allowed
  const countError = useMemo(() => {
    if (itemCount > 350000) {
      return `${itemCount} items selected (max 350,000). Please reduce timeframe.`;
    }
    return null;
  }, [itemCount]);

  const onSubmit = useCallback(
    async (formData: BackfillFormSchema) => {
      setApiError(false); // Clear previous errors
      try {
        await createBackfill({
          body: formData,
        });
        onCreateBackfill();
        closeModal();
        successToast('Started new backfill');
      } catch (error) {
        setApiError(error as Error);
      }
    },
    [createBackfill, onCreateBackfill, closeModal]
  );

  // Determine if the submit button should be disabled
  const isSubmitDisabled = useMemo(() => {
    return (
      createBackfillLoading ||
      !hasSelectedEntityTypes ||
      !itemCount ||
      itemCount > 350000 ||
      isCountLoading ||
      pendingRequest
    );
  }, [createBackfillLoading, hasSelectedEntityTypes, itemCount, isCountLoading, pendingRequest]);

  // Handler for Select All - selects all available entity types
  const handleSelectAll = useCallback(() => {
    const allEntityTypes = entityTypeSections[0]?.items.map((option) => option.id as BenchlingBackfillEntityType) || [];

    setValue('entity_types', allEntityTypes);
  }, [setValue, entityTypeSections]);

  // Memoize handlers for date pickers to prevent unnecessary rerenders
  const handleDateFromChange = useCallback(
    (value: string) => {
      setValue('date_from', value);
    },
    [setValue]
  );

  const handleDateToChange = useCallback(
    (value: string) => {
      setValue('date_to', value);
    },
    [setValue]
  );

  // Handler for study selection - adds to the collection
  const handleStudyChange = useCallback(
    (study?: StudyInfoV1) => {
      if (study) {
        // Add to the form value array if not already there
        const updatedStudyIds = studyApiIds?.includes(study.api_id)
          ? studyApiIds
          : [...(studyApiIds || Array<StudyApiId>()), study.api_id];
        setValue('study_api_ids', updatedStudyIds);

        // Update local state map with the new study
        setSelectedStudiesMap((prev) => ({
          ...prev,
          [study.api_id]: study,
        }));
      }
    },
    [setValue, studyApiIds]
  );

  // Memoized handler for StudyLookup onChange
  const handleStudyLookupChange = useCallback(
    (value: unknown) => {
      if (value && typeof value === 'object' && 'api_id' in value) {
        handleStudyChange(value as StudyInfoV1);
      }
    },
    [handleStudyChange]
  );

  // Memoized empty function for onBlur
  const handleStudyLookupBlur = useCallback(() => {}, []);

  // Get array of studies from the map for rendering and calculations
  const selectedStudiesArray = useMemo(() => Object.values(selectedStudiesMap), [selectedStudiesMap]);

  // Dynamic placeholder based on selected studies
  const studyPlaceholder = useMemo(() => {
    return selectedStudiesArray.length > 0 ? 'Select more studies to backfill' : 'All Studies';
  }, [selectedStudiesArray.length]);

  // Handler to remove a selected study
  const handleRemoveStudy = useCallback(
    (studyApiId: StudyApiId) => {
      // Remove from form state
      const updatedStudyIds = (studyApiIds || Array<StudyApiId>()).filter((id) => id !== studyApiId);
      setValue('study_api_ids', updatedStudyIds);

      // Remove from local state map
      setSelectedStudiesMap((prev) => {
        const newMap = { ...prev };
        delete newMap[studyApiId];
        return newMap;
      });
    },
    [setValue, studyApiIds]
  );

  // Create individual remove handlers to avoid inline functions
  const createRemoveHandler = useCallback(
    (studyApiId: StudyApiId) => () => handleRemoveStudy(studyApiId),
    [handleRemoveStudy]
  );

  // Consolidated handler for SearchSelect selection
  const handleSetSelected = useCallback(
    (selected: BenchlingBackfillEntityType[]) => {
      setValue('entity_types', selected);
    },
    [setValue]
  );

  // Handle individual item selection events
  const handleOnSelect = useCallback(
    ({ type, value }: SelectEvent) => {
      // This handler helps react-hook-form track changes properly
      const currentValues = entityTypes || Array<BenchlingBackfillEntityType>();
      if (type === 'ADDED' && !currentValues.includes(value)) {
        setValue('entity_types', [...currentValues, value]);
      } else if (type === 'REMOVED') {
        setValue(
          'entity_types',
          currentValues.filter((v) => v !== value)
        );
      }
    },
    [entityTypes, setValue]
  );

  // Memoize the status message to prevent recalculation on each render
  const statusMessage = useMemo(() => {
    if (_notNil(countError)) {
      return <div className="ph2 f6 dark-red">{countError}</div>;
    }

    if (!hasSelectedEntityTypes) {
      return <div className="ph2 f6 dark-gray">Select at least one item type.</div>;
    }

    // Show loading if either pendingRequest (pre-API call) or isCountLoading (during API call) is true
    if (pendingRequest || isCountLoading) {
      return <div className="ph2 f6 dark-gray">Fetching item count...</div>;
    }

    if (hasSelectedEntityTypes && countResponse?.body) {
      // Show count even if it's 0, as long as we have a valid response
      return <div className="ph2 f6 dark-gray">{itemCount} items</div>;
    }

    return <div className="ph2 f6 dark-gray">&nbsp;</div>; // Empty placeholder with same height
  }, [countError, isCountLoading, pendingRequest, hasSelectedEntityTypes, itemCount, countResponse]);

  return (
    <ModalContainer size="small">
      <ModalHeader
        title="Run backfill"
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        subText={'Choose a date range to include all data created within this period for backfilling.'}
        readMoreLink={RouteService.help('benchling_backfill')}
      />
      <FormProvider {...formMethods}>
        <div className="pv3 ph2">
          {apiError && <ApiErrorBanner className="mb3" error={apiError} />}

          <div className="flex mb0">
            <div className="w-50 ph2">
              <FormField name="date_from" label="From date" required testPrefix="from-date">
                <DatePickerNative
                  name="date_from"
                  value={dateFrom}
                  onChange={handleDateFromChange}
                  testId="from-date-input"
                />
              </FormField>
            </div>
            <div className="w-50 ph2">
              <FormField name="date_to" label="To date" required testPrefix="to-date">
                <DatePickerNative
                  name="date_to"
                  value={dateTo ?? DateUtils.dateNow()}
                  onChange={handleDateToChange}
                  testId="to-date-input"
                />
              </FormField>
            </div>
          </div>

          <div className="mb3 ph2">
            <FormField name="entity_types" label="Item types" required testPrefix="entity-types">
              <div className={styles.searchSelectContainer}>
                <SearchSelect
                  sections={entityTypeSections}
                  selected={entityTypes || []}
                  setSelected={handleSetSelected}
                  search={true}
                  enableSelectAll={true}
                  customSearchPlaceholderText="Search"
                  testPrefix="entity-types"
                  overflowHeight={320}
                  onSelect={handleOnSelect}
                  onSelectAll={handleSelectAll}
                  metaClassName="mt3 f7 dark-gray tr nowrap"
                />
              </div>
            </FormField>
          </div>

          <div className="mb3 ph2">
            <FormField name="study_api_ids" label="Studies to backfill" testPrefix="studies">
              <StudyLookup
                onChange={handleStudyLookupChange}
                onBlur={handleStudyLookupBlur}
                placeholder={studyPlaceholder}
                isStudyDisabled={isStudyDisabled}
              />

              {selectedStudiesArray.length > 0 && (
                <div className="flex flex-wrap mt2" data-test-element="selected-studies-container">
                  {selectedStudiesArray.map((study) => (
                    <div
                      key={study.api_id}
                      className="react-tagsinput-tag mr1 mb1 dib br1 pv1 ph2 lh-solid f7"
                      data-test-element={`study-pill-${study.api_id}`}
                    >
                      <span className="near-black">
                        {trunc(study.code ? `${study.code} · ${study.name}` : study.name, 30)}
                      </span>
                      <a
                        className="react-tagsinput-remove ml1 pointer"
                        onClick={createRemoveHandler(study.api_id)}
                        aria-label={`Remove study ${study.name}`}
                        data-test-element={`remove-study-${study.api_id}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </FormField>
          </div>

          <div className="ph2">
            <FormField name="backfill_count" label="Total items to backfill" testPrefix="backfill-count">
              <div className="h2 flex items-center">{statusMessage}</div>
            </FormField>
          </div>
        </div>
      </FormProvider>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        submitBtnText="Save"
        cancelBtnText="Cancel"
        submitButtonProps={{
          disabled: isSubmitDisabled,
          loading: createBackfillLoading,
        }}
      />
    </ModalContainer>
  );
});

export default CreateBackfill;
